import { i18n } from '../core/I18nManager';
import { AuthModal } from './AuthModal';
import { ApiClient } from '../core/ApiClient';

export class Navbar {
    private container: HTMLElement;
    private authModal: AuthModal;

    constructor(container: HTMLElement) {
        this.container = container;
        this.render();
        this.authModal = new AuthModal();
        this.initEvents();
        this.checkLoginStatus();
    }

    private render() {
        this.container.innerHTML = `
            <nav class="sticky top-0 z-50 w-full border-b border-stone-200/60 bg-stone-50/80 backdrop-blur-md">
                <div class="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <!-- Left: Logo -->
                    <div id="logo-btn" class="flex items-center gap-3 group cursor-pointer shrink-0">
                        <div class="w-8 h-8 border-2 border-stone-800 flex items-center justify-center rounded-sm rotate-45 group-hover:rotate-90 transition-transform duration-700 ease-out">
                            <div class="w-2.5 h-2.5 bg-stone-800 rounded-full"></div>
                        </div>
                        <div class="flex flex-col items-center leading-[0.8] pt-1">
                            <span class="text-xl font-black tracking-tighter text-stone-900 uppercase">Jigsaw</span>
                            <span class="text-[10px] font-bold tracking-[0.3em] text-stone-900 uppercase">Hex</span>
                        </div>
                    </div>

                    <!-- Right: All Actions -->
                    <div class="flex items-center gap-6 md:gap-8">
                        <!-- Search -->
                        <div id="nav-search" class="hidden lg:flex items-center bg-stone-100/50 border border-stone-200 pl-4 pr-1 py-1 rounded-full hover:border-stone-300 transition-colors cursor-text group">
                            <input type="text" id="searchInput" data-i18n="search_placeholder" placeholder="${i18n.t('search_placeholder') || '寻找静谧...'}" class="bg-transparent border-none outline-none text-xs w-36 placeholder:text-stone-400 text-stone-700">
                            <button id="searchBtn" class="flex items-center justify-center w-7 h-7 bg-stone-200/80 hover:bg-stone-800 hover:text-white rounded-full transition-all text-stone-500">
                                <iconify-icon icon="solar:magnifer-linear" width="16" stroke-width="1.5"></iconify-icon>
                            </button>
                        </div>

                        <!-- Action Group -->
                        <div class="flex items-center gap-5 md:gap-7">
                            <!-- Upload Link -->
                            <a href="upload.html" class="hidden sm:flex items-center gap-2 text-[11px] font-bold text-stone-500 hover:text-stone-900 transition-colors uppercase tracking-wider">
                                <iconify-icon icon="solar:upload-minimalistic-linear" width="18"></iconify-icon>
                                <span data-i18n="create_custom_puzzle">${i18n.t('create_custom_puzzle')}</span>
                            </a>

                            <!-- My Puzzles Link -->
                            <a href="my-puzzles.html" id="my-puzzles-link" class="hidden sm:flex items-center gap-2 text-[11px] font-bold text-stone-500 hover:text-stone-900 transition-colors uppercase tracking-wider">
                                <iconify-icon icon="solar:album-linear" width="18"></iconify-icon>
                                <span data-i18n="my_puzzles">${i18n.t('my_puzzles')}</span>
                            </a>

                            <!-- Language Selector -->
                            <div class="relative group">
                                <button id="langBtn" class="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100/50 border border-stone-200 hover:border-stone-300 transition-colors text-[10px] font-bold text-stone-600 uppercase tracking-widest">
                                    <iconify-icon icon="solar:global-linear" width="14"></iconify-icon>
                                    <span id="currentLangName">${this.getLangName(i18n.getCurrentLanguage())}</span>
                                    <iconify-icon icon="solar:alt-arrow-down-linear" width="10"></iconify-icon>
                                </button>
                                <div id="langDropdown" class="absolute right-0 top-full pt-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 z-50">
                                    <div class="w-32 bg-white/90 backdrop-blur-xl border border-stone-200 rounded-xl shadow-xl py-1 overflow-y-auto max-h-64 hide-scrollbar">
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="en">English</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="ja">日本語</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="ar">العربية</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="de">Deutsch</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="fr">Français</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="es">Español</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="pt">Português</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="it">Italiano</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="ru">Русский</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="zh-CN">简体中文</button>
                                        <button class="w-full text-left px-4 py-2 text-[11px] text-stone-600 hover:bg-stone-100 transition-colors" data-lang="zh-TW">繁體中文</button>
                                    </div>
                                </div>
                            </div>

                            <!-- User / Login -->
                            <button id="loginBtn" class="flex items-center gap-2 pl-2 pr-1 py-1 hover:bg-stone-100 rounded-full border border-transparent hover:border-stone-200 transition-all">
                                <span id="loginBtnText" class="text-xs font-bold text-stone-700" data-i18n="login">${i18n.t('login')}</span>
                                <div id="userAvatarContainer" class="w-7 h-7 bg-stone-200 rounded-full flex items-center justify-center text-stone-500 overflow-hidden shadow-sm">
                                    <iconify-icon id="userAvatarIcon" icon="solar:user-linear" width="16" stroke-width="1.5"></iconify-icon>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </nav>
        `;
        
        // 触发一次翻译扫描，确保动态注入的 data-i18n 元素被正确翻译
        i18n.translatePage();
    }

    private initEvents() {
        const logoBtn = this.container.querySelector('#logo-btn');
        logoBtn?.addEventListener('click', () => window.location.href = 'index.html');

        const loginBtn = this.container.querySelector('#loginBtn');
        loginBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.isLoggedIn()) {
                this.authModal.toggleUserDropdown();
            } else {
                this.authModal.show('login');
            }
        });

        // 拦截“我的拼图”点击
        const myPuzzlesLink = this.container.querySelector('#my-puzzles-link');
        myPuzzlesLink?.addEventListener('click', (e) => {
            if (!this.isLoggedIn()) {
                e.preventDefault(); // 阻止页面跳转
                this.authModal.show('login'); // 弹出登录框
            }
        });

        // Add global ref for other components to trigger auth
        (window as any).authModal = this.authModal;

        // 监听身份状态变更（处理登入登出）
        window.addEventListener('auth-status-changed', (e: any) => {
            if (e.detail.isLoggedIn) {
                this.updateNavbarUI(e.detail.user);
            } else {
                this.resetNavbarUI();
            }
        });

        const langButtons = this.container.querySelectorAll('#langDropdown button');
        langButtons.forEach(btn => {
            btn.addEventListener('click', async () => {
                const lang = (btn as HTMLElement).dataset.lang;
                if (lang) {
                    await i18n.setLanguage(lang as any);
                    this.updateLangText();
                    window.location.reload(); // Refresh to update all i18n
                }
            });
        });

        const searchInput = this.container.querySelector('#searchInput') as HTMLInputElement;
        const searchBtn = this.container.querySelector('#searchBtn');

        const triggerSearch = () => {
            const query = searchInput.value.trim();
            if (query) {
                window.location.href = `search.html?q=${encodeURIComponent(query)}`;
            }
        };

        if (searchInput && searchBtn) {
            // 点击放大镜搜索
            searchBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                triggerSearch();
            });

            // 按回车键搜索
            searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    triggerSearch();
                }
            });
        }
    }

    private updateLangText() {
        const langNameSpan = this.container.querySelector('#currentLangName');
        if (langNameSpan) {
            langNameSpan.textContent = this.getLangName(i18n.getCurrentLanguage());
        }
    }

    private getLangName(lang: string): string {
        const langMap: Record<string, string> = {
            'zh-CN': '中文',
            'zh-TW': '繁體',
            'en': 'EN',
            'ja': '日本語',
            'ar': 'العربية',
            'de': 'DE',
            'fr': 'FR',
            'es': 'ES',
            'pt': 'PT',
            'it': 'IT',
            'ru': 'RU'
        };
        return langMap[lang] || lang.toUpperCase();
    }

    public isLoggedIn(): boolean {
        return (this.authModal as any).user !== null;
    }

    private async checkLoginStatus() {
        try {
            const user = await ApiClient.get('/api/auth/me', undefined, { skipErrorToast: true }) as any;
            if (user) {
                this.authModal.updateUserUI(user);
                this.updateNavbarUI(user);
                window.dispatchEvent(new CustomEvent('auth-status-changed', { detail: { isLoggedIn: true, user } }));
            } else {
                this.handleLoggedOut();
            }
        } catch (e) {
            this.handleLoggedOut();
        }
    }

    private updateNavbarUI(user: any) {
        const text = this.container.querySelector('#loginBtnText');
        const avatarContainer = this.container.querySelector('#userAvatarContainer');
        if (text) text.textContent = user.username;
        if (avatarContainer) {
            const url = user.avatar_url || user.avatar;
            if (url) {
                avatarContainer.innerHTML = `<img src="${url}" class="w-full h-full object-cover">`;
            }
        }
    }

    private resetNavbarUI() {
        const text = this.container.querySelector('#loginBtnText');
        const avatarContainer = this.container.querySelector('#userAvatarContainer');
        if (text) text.textContent = i18n.t('login') || '登录';
        if (avatarContainer) {
            avatarContainer.innerHTML = `<iconify-icon id="userAvatarIcon" icon="solar:user-linear" width="14" stroke-width="1.5"></iconify-icon>`;
        }
    }

    private handleLoggedOut() {
        this.authModal.updateUserUI(null);
        window.dispatchEvent(new CustomEvent('auth-status-changed', { detail: { isLoggedIn: false, user: null } }));
    }

    private getCookie(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
        return null;
    }
}
